import { useToastOnFail } from "@/components/molecules/Toast/use-toast";
import type { CredentialsMetaResponse as GeneratedCredentialsMetaResponse } from "@/app/api/__generated__/models/credentialsMetaResponse";
import {
  APIKeyCredentials,
  CredentialsDeleteNeedConfirmationResponse,
  CredentialsDeleteResponse,
  CredentialsMetaResponse,
  CredentialsProviderName,
  HostScopedCredentials,
  UserPasswordCredentials,
} from "@/lib/autogpt-server-api";
import { getGetV1ListCredentialsQueryKey } from "@/app/api/__generated__/endpoints/integrations/integrations";
import {
  postV2ExchangeOauthCodeForMcpTokens,
  postV2StoreABearerTokenForAnMcpServer,
} from "@/app/api/__generated__/endpoints/mcp/mcp";
import { useBackendAPI } from "@/lib/autogpt-server-api/context";
import { useAuth } from "@/lib/auth/hooks/useAuth";
import { toDisplayName } from "@/providers/agent-credentials/helper";
import { hashKey, useQueryClient } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";

type APIKeyCredentialsCreatable = Omit<
  APIKeyCredentials,
  "id" | "provider" | "type"
>;

type UserPasswordCredentialsCreatable = Omit<
  UserPasswordCredentials,
  "id" | "provider" | "type"
>;

type HostScopedCredentialsCreatable = Omit<
  HostScopedCredentials,
  "id" | "provider" | "type"
>;

export type CredentialsProviderData = {
  provider: CredentialsProviderName;
  providerName: string;
  savedCredentials: CredentialsMetaResponse[];
  /** Whether this provider has platform credits available (system credentials) */
  isSystemProvider: boolean;
  oAuthCallback: (
    code: string,
    state_token: string,
  ) => Promise<CredentialsMetaResponse>;
  /** MCP-specific OAuth callback that uses dynamic per-server OAuth discovery. */
  mcpOAuthCallback: (
    code: string,
    state_token: string,
    iss?: string,
  ) => Promise<CredentialsMetaResponse>;
  /** Stores a manually entered MCP credential for a server without OAuth. */
  mcpStoreToken: (
    server_url: string,
    token: string,
  ) => Promise<CredentialsMetaResponse>;
  createAPIKeyCredentials: (
    credentials: APIKeyCredentialsCreatable,
  ) => Promise<CredentialsMetaResponse>;
  createUserPasswordCredentials: (
    credentials: UserPasswordCredentialsCreatable,
  ) => Promise<CredentialsMetaResponse>;
  createHostScopedCredentials: (
    credentials: HostScopedCredentialsCreatable,
  ) => Promise<CredentialsMetaResponse>;
  deleteCredentials: (
    id: string,
    force?: boolean,
  ) => Promise<
    CredentialsDeleteResponse | CredentialsDeleteNeedConfirmationResponse
  >;
};

export type CredentialsProvidersContextType = {
  [key in CredentialsProviderName]?: CredentialsProviderData;
};

export const CredentialsProvidersContext =
  createContext<CredentialsProvidersContextType | null>(null);

/**
 * Replace an existing credential entry (matched by id) or append a new one,
 * producing a fresh providers map.  Used by both OAuth callback paths and
 * the "create credential" flows so a scope upgrade doesn't duplicate rows
 * in the sidebar.  Pure so it can be exercised directly in tests without
 * dragging in React state machinery.
 */
export function upsertProviderCredentials(
  prev: CredentialsProvidersContextType | null,
  provider: CredentialsProviderName,
  credentials: CredentialsMetaResponse,
): CredentialsProvidersContextType | null {
  if (!prev || !prev[provider]) return prev;

  const existing = prev[provider].savedCredentials;
  const idx = existing.findIndex((c) => c.id === credentials.id);
  const updated =
    idx >= 0
      ? existing.map((c, i) => (i === idx ? credentials : c))
      : [...existing, credentials];

  return {
    ...prev,
    [provider]: {
      ...prev[provider],
      savedCredentials: updated,
    },
  };
}

/**
 * Append credentials a freshly-loaded list does not carry yet: a
 * `listCredentials()` request already in flight when one is created predates
 * it, and publishing that response unmodified drops it. An id the list does
 * carry needs no pending copy: the caller retires it in the same pass.
 */
export function mergePendingCredentials(
  loaded: CredentialsMetaResponse[],
  pending: CredentialsMetaResponse[] | undefined,
): CredentialsMetaResponse[] {
  if (!pending?.length) return loaded;
  const loadedIds = new Set(loaded.map((c) => c.id));
  return [...loaded, ...pending.filter((c) => !loadedIds.has(c.id))];
}

/**
 * Imperative actions on the credentials context.  Split out so the data
 * context can stay read-only for consumers that only render the list —
 * components that need to re-fetch after a side effect (e.g. Ayrshare
 * SSO provisioning a managed cred) opt in explicitly.
 */
export const CredentialsActionsContext = createContext<{
  reload: () => void;
  /**
   * Add a just-created credential without waiting for a re-fetch. A flow that
   * mints one outside this provider (device auth polls the backend directly)
   * must call this, or `useCredentialsInput` reports it as *removed*.
   */
  upsert: (
    provider: CredentialsProviderName,
    credentials: GeneratedCredentialsMetaResponse,
  ) => void;
} | null>(null);

export default function CredentialsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [providers, setProviders] =
    useState<CredentialsProvidersContextType | null>(null);
  const [providerNames, setProviderNames] = useState<string[]>([]);
  const [systemProviders, setSystemProviders] = useState<Set<string>>(
    new Set(),
  );
  const { isLoggedIn, isUserLoading } = useAuth();
  const api = useBackendAPI();
  const onFailToast = useToastOnFail();
  const queryClient = useQueryClient();

  // Held until a load returns them: the store can still be null when the upsert
  // lands, and an in-flight list can resolve with a list that predates it.
  const pendingUpsertsRef = useRef<
    Partial<Record<CredentialsProviderName, CredentialsMetaResponse[]>>
  >({});
  // Only the newest list request may publish or retire; an older one can
  // resolve last with a response that predates an upsert.
  const loadGenerationRef = useRef(0);
  // The handlers the published provider entries close over. A load may keep an
  // entry whose credentials did not change only while these are the same, so
  // a reused entry never calls a stale handler.
  const entryHandlersRef = useRef<unknown[]>([]);

  const upsertCredentials = useCallback(
    (
      provider: CredentialsProviderName,
      credentials: CredentialsMetaResponse,
    ) => {
      const pending = pendingUpsertsRef.current[provider] ?? [];
      pendingUpsertsRef.current[provider] = [
        ...pending.filter(
          (c: CredentialsMetaResponse) => c.id !== credentials.id,
        ),
        credentials,
      ];
      setProviders((prev) =>
        upsertProviderCredentials(prev, provider, credentials),
      );
    },
    [setProviders],
  );

  /**
   * Upsert a credential straight off the API. The generated model marks several
   * fields nullable where the store's model uses `undefined`.
   */
  const upsertFromApi = useCallback(
    (
      provider: CredentialsProviderName,
      credentials: GeneratedCredentialsMetaResponse,
    ) => {
      upsertCredentials(provider, {
        ...credentials,
        title: credentials.title ?? undefined,
        scopes: credentials.scopes ?? undefined,
        username: credentials.username ?? undefined,
        host: credentials.host ?? undefined,
      } as CredentialsMetaResponse);
    },
    [upsertCredentials],
  );

  /** Wraps `BackendAPI.oAuthCallback`, and adds the result to the internal credentials store. */
  const oAuthCallback = useCallback(
    async (
      provider: CredentialsProviderName,
      code: string,
      state_token: string,
    ): Promise<CredentialsMetaResponse> => {
      try {
        const credsMeta = await api.oAuthCallback(provider, code, state_token);
        upsertCredentials(provider, credsMeta);
        return credsMeta;
      } catch (error) {
        onFailToast("complete OAuth authentication")(error);
        throw error;
      }
    },
    [api, upsertCredentials, onFailToast],
  );

  /** Exchanges an MCP OAuth code for tokens and adds the result to the internal credentials store. */
  const mcpOAuthCallback = useCallback(
    async (
      code: string,
      state_token: string,
      iss?: string,
    ): Promise<CredentialsMetaResponse> => {
      try {
        const response = await postV2ExchangeOauthCodeForMcpTokens({
          code,
          state_token,
          iss,
        });
        if (response.status !== 200) throw response.data;
        const credsMeta: CredentialsMetaResponse = {
          ...response.data,
          title: response.data.title ?? undefined,
          scopes: response.data.scopes ?? undefined,
          username: response.data.username ?? undefined,
          host: response.data.host ?? undefined,
        };
        upsertCredentials("mcp", credsMeta);
        return credsMeta;
      } catch (error) {
        onFailToast("complete MCP OAuth authentication")(error);
        throw error;
      }
    },
    [upsertCredentials, onFailToast],
  );

  /** Stores a manual MCP credential, and adds the result to the internal credentials store. */
  const mcpStoreToken = useCallback(
    async (
      server_url: string,
      token: string,
    ): Promise<CredentialsMetaResponse> => {
      const response = await postV2StoreABearerTokenForAnMcpServer({
        server_url,
        token,
      });
      if (response.status !== 200) throw response.data;
      const credsMeta: CredentialsMetaResponse = {
        ...response.data,
        title: response.data.title ?? undefined,
        scopes: response.data.scopes ?? undefined,
        username: response.data.username ?? undefined,
        host: response.data.host ?? undefined,
      };
      upsertCredentials("mcp", credsMeta);
      return credsMeta;
    },
    [upsertCredentials],
  );

  /** Wraps `BackendAPI.createAPIKeyCredentials`, and adds the result to the internal credentials store. */
  const createAPIKeyCredentials = useCallback(
    async (
      provider: CredentialsProviderName,
      credentials: APIKeyCredentialsCreatable,
    ): Promise<CredentialsMetaResponse> => {
      try {
        const credsMeta = await api.createAPIKeyCredentials({
          provider,
          ...credentials,
        });
        upsertCredentials(provider, credsMeta);
        return credsMeta;
      } catch (error) {
        onFailToast("create API key credentials")(error);
        throw error;
      }
    },
    [api, upsertCredentials, onFailToast],
  );

  /** Wraps `BackendAPI.createUserPasswordCredentials`, and adds the result to the internal credentials store. */
  const createUserPasswordCredentials = useCallback(
    async (
      provider: CredentialsProviderName,
      credentials: UserPasswordCredentialsCreatable,
    ): Promise<CredentialsMetaResponse> => {
      try {
        const credsMeta = await api.createUserPasswordCredentials({
          provider,
          ...credentials,
        });
        upsertCredentials(provider, credsMeta);
        return credsMeta;
      } catch (error) {
        onFailToast("create user/password credentials")(error);
        throw error;
      }
    },
    [api, upsertCredentials, onFailToast],
  );

  /** Wraps `BackendAPI.createHostScopedCredentials`, and adds the result to the internal credentials store. */
  const createHostScopedCredentials = useCallback(
    async (
      provider: CredentialsProviderName,
      credentials: HostScopedCredentialsCreatable,
    ): Promise<CredentialsMetaResponse> => {
      try {
        const credsMeta = await api.createHostScopedCredentials({
          provider,
          ...credentials,
        });
        upsertCredentials(provider, credsMeta);
        return credsMeta;
      } catch (error) {
        onFailToast("create host-scoped credentials")(error);
        throw error;
      }
    },
    [api, upsertCredentials, onFailToast],
  );

  /** Wraps `BackendAPI.deleteCredentials`, and removes the credentials from the internal store. */
  const deleteCredentials = useCallback(
    async (
      provider: CredentialsProviderName,
      id: string,
      force: boolean = false,
    ): Promise<
      CredentialsDeleteResponse | CredentialsDeleteNeedConfirmationResponse
    > => {
      try {
        const result = await api.deleteCredentials(provider, id, force);
        if (!result.deleted) {
          return result;
        }
        // A list request that started before the delete would otherwise
        // resolve afterwards with the deleted credential and republish it.
        loadGenerationRef.current += 1;
        // A credential deleted while still pending would otherwise be merged
        // straight back into the picker by the next load.
        const pendingForProvider = pendingUpsertsRef.current[provider];
        if (pendingForProvider?.length) {
          const kept = pendingForProvider.filter(
            (c: CredentialsMetaResponse) => c.id !== id,
          );
          if (kept.length) {
            pendingUpsertsRef.current[provider] = kept;
          } else {
            delete pendingUpsertsRef.current[provider];
          }
        }
        setProviders((prev) => {
          if (!prev || !prev[provider]) return prev;

          return {
            ...prev,
            [provider]: {
              ...prev[provider]!,
              savedCredentials: prev[provider]!.savedCredentials.filter(
                (cred) => cred.id !== id,
              ),
            },
          };
        });
        return result;
      } catch (error) {
        onFailToast("delete credentials")(error);
        throw error;
      }
    },
    [api, onFailToast],
  );

  const loadCredentials = useCallback(() => {
    if (!isLoggedIn) {
      // Left in place these would be merged into the *next* user's list,
      // exposing the previous account's credential metadata.
      pendingUpsertsRef.current = {};
      // Retire any in-flight request so its response cannot publish over the
      // logged-out state.
      loadGenerationRef.current += 1;
    }
    if (!isLoggedIn || providerNames.length === 0) {
      // null is the sole "still loading" sentinel; an empty object means
      // "loaded, and this user has no providers". Keeping those distinct
      // matters because consumers render an unavailable state from the
      // latter, and showing that to an entitled user is wrong.
      //
      // Logged out, once auth has settled: genuinely empty. isLoggedIn is
      // false while the auth store initializes too, hence the isUserLoading
      // guard — without it a logged-in user briefly gets the empty map.
      if (!isUserLoading && !isLoggedIn) {
        setProviders({});
        return;
      }
      // Logged in but provider names haven't arrived yet. If a previous
      // logout left the empty map published, clear it back to null so this
      // reads as loading rather than "nothing is available to you".
      if (isLoggedIn) setProviders(null);
      return;
    }

    const generation = ++loadGenerationRef.current;

    api
      .listCredentials()
      .then((response) => {
        // Stale: publishing would overwrite fresher state, and retiring from
        // it would strand credentials the newer response has not returned.
        if (generation !== loadGenerationRef.current) return;
        const credentialsByProvider = response.reduce(
          (acc, cred) => {
            if (!acc[cred.provider]) {
              acc[cred.provider] = [];
            }
            acc[cred.provider].push(cred);
            return acc;
          },
          {} as Record<CredentialsProviderName, CredentialsMetaResponse[]>,
        );

        const entryHandlers = [
          oAuthCallback,
          mcpOAuthCallback,
          mcpStoreToken,
          createAPIKeyCredentials,
          createUserPasswordCredentials,
          createHostScopedCredentials,
          deleteCredentials,
        ];
        const canReuseEntries = isEqual(
          entryHandlersRef.current,
          entryHandlers,
        );
        entryHandlersRef.current = entryHandlers;

        setProviders((prev) => {
          const loadedEntries = Object.fromEntries(
            providerNames.map((provider) => {
              const loaded = credentialsByProvider[provider] ?? [];
              const pending = pendingUpsertsRef.current[provider];
              // One predicate drives both the merge and the retirement: an id
              // the server returned is caught up, so its pending copy is
              // neither merged in nor kept.
              const loadedIds = new Set(
                loaded.map((c: CredentialsMetaResponse) => c.id),
              );
              const stillPending = pending?.filter(
                (c: CredentialsMetaResponse) => !loadedIds.has(c.id),
              );
              const providerCredentials = mergePendingCredentials(
                loaded,
                stillPending,
              );
              if (pending?.length) {
                if (stillPending?.length) {
                  pendingUpsertsRef.current[provider] = stillPending;
                } else {
                  delete pendingUpsertsRef.current[provider];
                }
              }

              // Every invalidate/success refetch lands here; an entry that did
              // not change keeps its identity so consumers skip a re-render.
              const isSystemProvider = systemProviders.has(provider);
              const existing = prev?.[provider];
              if (
                canReuseEntries &&
                existing &&
                existing.isSystemProvider === isSystemProvider &&
                isEqual(existing.savedCredentials, providerCredentials)
              ) {
                return [provider, existing];
              }

              return [
                provider,
                {
                  provider,
                  providerName: toDisplayName(provider),
                  savedCredentials: providerCredentials,
                  isSystemProvider,
                  oAuthCallback: (code: string, state_token: string) =>
                    oAuthCallback(provider, code, state_token),
                  mcpOAuthCallback,
                  mcpStoreToken,
                  createAPIKeyCredentials: (
                    credentials: APIKeyCredentialsCreatable,
                  ) => createAPIKeyCredentials(provider, credentials),
                  createUserPasswordCredentials: (
                    credentials: UserPasswordCredentialsCreatable,
                  ) => createUserPasswordCredentials(provider, credentials),
                  createHostScopedCredentials: (
                    credentials: HostScopedCredentialsCreatable,
                  ) => createHostScopedCredentials(provider, credentials),
                  deleteCredentials: (id: string, force: boolean = false) =>
                    deleteCredentials(provider, id, force),
                } satisfies CredentialsProviderData,
              ];
            }),
          );
          const unchanged =
            prev !== null &&
            Object.entries(loadedEntries).every(
              ([provider, entry]) =>
                prev[provider as CredentialsProviderName] === entry,
            );
          return unchanged ? prev : { ...prev, ...loadedEntries };
        });
      })
      .catch(onFailToast("load credentials"));
  }, [
    api,
    isLoggedIn,
    isUserLoading,
    providerNames,
    systemProviders,
    createAPIKeyCredentials,
    createUserPasswordCredentials,
    createHostScopedCredentials,
    deleteCredentials,
    oAuthCallback,
    mcpOAuthCallback,
    mcpStoreToken,
    onFailToast,
  ]);

  // Fetch provider names and system providers after authentication
  useEffect(() => {
    if (!isLoggedIn) {
      setProviderNames([]);
      setSystemProviders(new Set());
      return;
    }

    let isCurrent = true;
    Promise.all([api.listProviders(), api.listSystemProviders()])
      .then(([names, systemList]) => {
        if (!isCurrent) return;
        setProviderNames(names);
        setSystemProviders(new Set(systemList));
      })
      .catch((error) => {
        if (isCurrent) onFailToast("Load provider names")(error);
      });

    return () => {
      isCurrent = false;
    };
  }, [api, isLoggedIn, onFailToast]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  // Keep the in-memory provider state in sync with React Query: any code that
  // invalidates the credentials list query (e.g. settings v2 create/delete
  // flows using generated hooks) will trigger a reload here, so the builder
  // never sees stale credentials without callers having to opt in.
  //
  // Match by `queryHash` (computed via the public `hashKey` helper) instead
  // of comparing `queryKey[0]` so any future parameterisation of the key
  // still works. Listen for both `invalidate` (set isInvalidated=true) and
  // `success` (refetch landed) actions so we cover active and inactive
  // queries — for active queries the refetch following invalidate produces
  // the `success` event with fresh data; for inactive queries only the
  // `invalidate` action fires.
  useEffect(() => {
    const targetHash = hashKey(getGetV1ListCredentialsQueryKey());
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryHash !== targetHash) return;
      if (event.type !== "updated") return;
      const actionType = event.action.type;
      if (actionType === "invalidate" || actionType === "success") {
        loadCredentials();
      }
    });
    return unsubscribe;
  }, [queryClient, loadCredentials]);

  const actions = useMemo(
    () => ({ reload: loadCredentials, upsert: upsertFromApi }),
    [loadCredentials, upsertFromApi],
  );

  return (
    <CredentialsActionsContext.Provider value={actions} data-sentry-element="CredentialsActionsContext.Provider" data-sentry-component="CredentialsProvider" data-sentry-source-file="credentials-provider.tsx">
      <CredentialsProvidersContext.Provider value={providers} data-sentry-element="CredentialsProvidersContext.Provider" data-sentry-source-file="credentials-provider.tsx">
        {children}
      </CredentialsProvidersContext.Provider>
    </CredentialsActionsContext.Provider>
  );
}
